import React, { useState, useContext } from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet, ScrollView, TextInput,
  KeyboardAvoidingView, Platform, Alert,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useToast } from 'react-native-toast-notifications';
import { IconButton } from 'react-native-paper';
import { useGame } from '../../context/GameContext';
import { SCREEN_NAMES } from '../../utils/constant';
import { PACK_LABELS } from '../../utils/gameWords';
import { theme } from '../Common.style';

const PACKS = Object.entries(PACK_LABELS).map(([value, label]) => ({ value, label }));
const MODES = [
  { value: 'LOCAL', label: 'Local (un appareil)', icon: 'cellphone', desc: 'Passez le téléphone à chaque joueur' },
  { value: 'COPRESENT', label: 'Même endroit', icon: 'account-group', desc: 'Chaque joueur sur son appareil' },
  { value: 'REMOTE', label: 'À distance', icon: 'web', desc: 'Chat + messages vocaux intégrés' },
];

export default function GameLobbyScreen() {
  const navigation = useNavigation();
  const toast = useToast();
  const { createRoom, joinRoom, startLocalGame } = useGame();

  const [tab, setTab] = useState('create'); // 'create' | 'join'
  const [selectedPack, setSelectedPack] = useState('everyday');
  const [selectedMode, setSelectedMode] = useState('COPRESENT');
  const [joinCode, setJoinCode] = useState('');
  const [localPlayerInput, setLocalPlayerInput] = useState('');
  const [localPlayers, setLocalPlayers] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleCreate = async () => {
    if (selectedMode === 'LOCAL') {
      if (localPlayers.length < 3) {
        toast.show('Minimum 3 joueurs pour jouer', { type: 'warning' });
        return;
      }
      startLocalGame(selectedPack, localPlayers);
      navigation.navigate(SCREEN_NAMES.GAME_WAITING_ROOM);
      return;
    }

    try {
      setLoading(true);
      await createRoom(selectedPack, selectedMode);
      navigation.navigate(SCREEN_NAMES.GAME_WAITING_ROOM);
    } catch (e) {
      toast.show(e.message || 'Erreur lors de la création', { type: 'danger' });
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    if (joinCode.length !== 4) {
      toast.show('Entrez un code à 4 lettres', { type: 'warning' });
      return;
    }
    try {
      setLoading(true);
      await joinRoom(joinCode.toUpperCase());
      navigation.navigate(SCREEN_NAMES.GAME_WAITING_ROOM);
    } catch (e) {
      toast.show(e.message || 'Salle introuvable', { type: 'danger' });
    } finally {
      setLoading(false);
    }
  };

  const addLocalPlayer = () => {
    const name = localPlayerInput.trim();
    if (!name) return;
    if (localPlayers.length >= 10) {
      toast.show('Maximum 10 joueurs', { type: 'warning' });
      return;
    }
    setLocalPlayers((prev) => [...prev, { name }]);
    setLocalPlayerInput('');
  };

  return (
    <KeyboardAvoidingView style={styles.flex} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
      <ScrollView contentContainerStyle={styles.container}>
        <Text style={styles.title}>🕵️ Jeu de l'Imposteur</Text>
        <Text style={styles.subtitle}>Un mot secret, un imposteur à démasquer</Text>

        <View style={styles.tabs}>
          <TouchableOpacity
            style={[styles.tab, tab === 'create' && styles.tabActive]}
            onPress={() => setTab('create')}
          >
            <Text style={[styles.tabText, tab === 'create' && styles.tabTextActive]}>Créer</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.tab, tab === 'join' && styles.tabActive]}
            onPress={() => setTab('join')}
          >
            <Text style={[styles.tabText, tab === 'join' && styles.tabTextActive]}>Rejoindre</Text>
          </TouchableOpacity>
        </View>

        {tab === 'create' ? (
          <>
            <Text style={styles.sectionLabel}>Thème des mots</Text>
            <View style={styles.optionRow}>
              {PACKS.map((p) => (
                <TouchableOpacity
                  key={p.value}
                  style={[styles.chip, selectedPack === p.value && styles.chipActive]}
                  onPress={() => setSelectedPack(p.value)}
                >
                  <Text style={[styles.chipText, selectedPack === p.value && styles.chipTextActive]}>
                    {p.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>

            <Text style={styles.sectionLabel}>Mode de jeu</Text>
            {MODES.map((m) => (
              <TouchableOpacity
                key={m.value}
                style={[styles.modeCard, selectedMode === m.value && styles.modeCardActive]}
                onPress={() => setSelectedMode(m.value)}
              >
                <IconButton
                  icon={m.icon}
                  size={24}
                  iconColor={selectedMode === m.value ? theme.colors.primary : '#666'}
                />
                <View style={styles.modeTextBlock}>
                  <Text style={[styles.modeLabel, selectedMode === m.value && { color: theme.colors.primary }]}>
                    {m.label}
                  </Text>
                  <Text style={styles.modeDesc}>{m.desc}</Text>
                </View>
              </TouchableOpacity>
            ))}

            {selectedMode === 'LOCAL' && (
              <>
                <Text style={styles.sectionLabel}>Joueurs ({localPlayers.length}/10)</Text>
                {localPlayers.map((p, i) => (
                  <View key={i} style={styles.playerRow}>
                    <Text style={styles.playerName}>{p.name}</Text>
                    <TouchableOpacity onPress={() => setLocalPlayers((prev) => prev.filter((_, idx) => idx !== i))}>
                      <IconButton icon="close" size={16} />
                    </TouchableOpacity>
                  </View>
                ))}
                <View style={styles.addPlayerRow}>
                  <TextInput
                    style={styles.playerInput}
                    placeholder="Prénom du joueur"
                    value={localPlayerInput}
                    onChangeText={setLocalPlayerInput}
                    onSubmitEditing={addLocalPlayer}
                    returnKeyType="done"
                  />
                  <TouchableOpacity style={styles.addBtn} onPress={addLocalPlayer}>
                    <Text style={styles.addBtnText}>+</Text>
                  </TouchableOpacity>
                </View>
              </>
            )}

            <TouchableOpacity
              style={[styles.primaryBtn, loading && styles.btnDisabled]}
              onPress={handleCreate}
              disabled={loading}
            >
              <Text style={styles.primaryBtnText}>
                {loading ? 'Création...' : 'Créer la partie'}
              </Text>
            </TouchableOpacity>
          </>
        ) : (
          <>
            <Text style={styles.sectionLabel}>Code de la salle (4 lettres)</Text>
            <TextInput
              style={styles.codeInput}
              placeholder="XKRT"
              value={joinCode}
              onChangeText={(t) => setJoinCode(t.toUpperCase())}
              maxLength={4}
              autoCapitalize="characters"
            />
            <TouchableOpacity
              style={[styles.primaryBtn, loading && styles.btnDisabled]}
              onPress={handleJoin}
              disabled={loading}
            >
              <Text style={styles.primaryBtnText}>
                {loading ? 'Connexion...' : 'Rejoindre'}
              </Text>
            </TouchableOpacity>
          </>
        )}
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1, backgroundColor: '#fff' },
  container: { padding: 20, paddingBottom: 40 },
  title: { fontSize: 28, fontWeight: 'bold', textAlign: 'center', marginTop: 10 },
  subtitle: { textAlign: 'center', color: '#666', marginBottom: 24 },
  tabs: { flexDirection: 'row', borderRadius: 12, overflow: 'hidden', marginBottom: 24, borderWidth: 1, borderColor: '#ddd' },
  tab: { flex: 1, paddingVertical: 12, alignItems: 'center', backgroundColor: '#fff' },
  tabActive: { backgroundColor: theme.colors.primary },
  tabText: { fontWeight: '600', color: '#666' },
  tabTextActive: { color: '#fff' },
  sectionLabel: { fontWeight: '700', fontSize: 14, marginBottom: 8, marginTop: 16, color: '#333' },
  optionRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  chip: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 20, backgroundColor: '#f0f0f0', marginBottom: 4 },
  chipActive: { backgroundColor: theme.colors.primary },
  chipText: { color: '#555', fontSize: 13 },
  chipTextActive: { color: '#fff' },
  modeCard: { flexDirection: 'row', alignItems: 'center', borderRadius: 12, borderWidth: 1.5, borderColor: '#ddd', marginBottom: 8, paddingRight: 12 },
  modeCardActive: { borderColor: theme.colors.primary, backgroundColor: theme.colors.primary + '10' },
  modeTextBlock: { flex: 1 },
  modeLabel: { fontWeight: '600', fontSize: 14, color: '#333' },
  modeDesc: { fontSize: 12, color: '#888', marginTop: 2 },
  playerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 4 },
  playerName: { fontSize: 15, color: '#333' },
  addPlayerRow: { flexDirection: 'row', gap: 8, marginTop: 4 },
  playerInput: { flex: 1, borderWidth: 1, borderColor: '#ddd', borderRadius: 10, paddingHorizontal: 12, paddingVertical: 8 },
  addBtn: { width: 40, height: 40, borderRadius: 20, backgroundColor: theme.colors.primary, alignItems: 'center', justifyContent: 'center' },
  addBtnText: { color: '#fff', fontSize: 22, lineHeight: 26 },
  codeInput: { fontSize: 32, textAlign: 'center', letterSpacing: 12, borderWidth: 2, borderColor: '#ddd', borderRadius: 12, paddingVertical: 16, marginBottom: 8, fontWeight: 'bold' },
  primaryBtn: { backgroundColor: theme.colors.primary, borderRadius: 14, paddingVertical: 16, alignItems: 'center', marginTop: 24 },
  btnDisabled: { opacity: 0.6 },
  primaryBtnText: { color: '#fff', fontWeight: '700', fontSize: 17 },
});
